import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Pencil, Music2, Instagram, Facebook } from "lucide-react";

const PLATFORM_ICON = {
    tiktok: Music2,
    instagram: Instagram,
    facebook: Facebook,
};

const PLATFORM_ACCENT = {
    tiktok: "text-[#00F5D4]",
    instagram: "text-[#FF5470]",
    facebook: "text-[#2F58CD]",
};

const PLATFORM_DOT = {
    tiktok: "bg-[#00F5D4]",
    instagram: "bg-[#FF5470]",
    facebook: "bg-[#2F58CD]",
};

export default function SuggestionSection({
    kind, // "title" | "caption"
    label,
    items = [],
    selectedIdx,
    edited,
    onSelect,
    onEdit,
    platformLabel,
}) {
    return (
        <section className="flex flex-col gap-5" data-testid={`${kind}-section`}>
            <div className="flex items-center justify-between">
                <h2 className="text-base font-medium tracking-[0.02em] text-[#E8ECF5]">
                    {label}
                </h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8A93A6]">
                    pick one
                </span>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                {items.map((item, idx) => (
                    <SuggestionCard
                        key={`${kind}-${idx}`}
                        kind={kind}
                        idx={idx}
                        item={item}
                        editedValue={edited?.[idx]}
                        isSelected={selectedIdx === idx}
                        onSelect={() => onSelect(idx)}
                        onEdit={(v) => onEdit(idx, v)}
                        platformLabel={platformLabel}
                    />
                ))}
            </div>
        </section>
    );
}

function SuggestionCard({
    kind,
    idx,
    item,
    editedValue,
    isSelected,
    onSelect,
    onEdit,
    platformLabel,
}) {
    const [editing, setEditing] = useState(false);
    const platform = item.platform;
    const Icon = PLATFORM_ICON[platform] || Music2;
    const accent = PLATFORM_ACCENT[platform] || "text-[#E8ECF5]";
    const dot = PLATFORM_DOT[platform] || "bg-[#2F58CD]";
    const value = editedValue !== undefined ? editedValue : item.text;

    return (
        <motion.div
            layout
            whileTap={{ scale: 0.98 }}
            onClick={() => !editing && onSelect()}
            className={[
                "group relative flex h-full cursor-pointer flex-col gap-3 overflow-hidden rounded-xl border bg-[#111726] p-5 transition-all duration-200",
                isSelected
                    ? "border-[#00F5D4] bg-[#1A2235] shadow-[0_0_24px_rgba(0,245,212,0.18)]"
                    : "border-[#1F2A40] hover:border-[#2F58CD]/60 hover:bg-[#1A2235]/60",
            ].join(" ")}
            data-testid={`${kind}-card-${idx}`}
            data-platform={platform}
            data-selected={isSelected ? "true" : "false"}
        >
            <div className="flex items-center justify-between">
                <span
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8A93A6]"
                    data-testid={`${kind}-platform-${idx}`}
                >
                    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                    <Icon className={`h-3 w-3 ${accent}`} />
                    {platformLabel?.[platform] || platform}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditing((s) => !s);
                        }}
                        className="rounded-md p-1 text-[#8A93A6] transition-colors hover:bg-[#0B0F19] hover:text-[#00F5D4]"
                        data-testid={`${kind}-edit-btn-${idx}`}
                        aria-label="Edit"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {isSelected && (
                        <motion.span
                            initial={{ scale: 0, rotate: -45 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", stiffness: 400, damping: 22 }}
                            className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00F5D4] text-[#0B0F19]"
                            data-testid={`${kind}-check-${idx}`}
                        >
                            <Check className="h-3 w-3" strokeWidth={3} />
                        </motion.span>
                    )}
                </div>
            </div>

            {editing ? (
                <textarea
                    autoFocus
                    value={value}
                    onChange={(e) => onEdit(e.target.value)}
                    onBlur={() => setEditing(false)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            setEditing(false);
                        }
                    }}
                    className={[
                        "w-full resize-y rounded-md border border-[#1F2A40] bg-[#0B0F19] p-2 text-[#E8ECF5] outline-none focus:border-[#00F5D4] focus:ring-1 focus:ring-[#00F5D4]/40",
                        kind === "title"
                            ? "min-h-[60px] text-base font-medium"
                            : "min-h-[120px] text-sm leading-relaxed",
                    ].join(" ")}
                    data-testid={`${kind}-edit-input-${idx}`}
                />
            ) : (
                <p
                    className={
                        kind === "title"
                            ? "text-[15px] font-medium leading-snug text-[#E8ECF5]"
                            : "text-sm leading-relaxed text-[#E8ECF5]/90"
                    }
                    data-testid={`${kind}-text-${idx}`}
                >
                    {value}
                </p>
            )}

            {kind === "title" && item.rationale && (
                <p
                    className="font-mono text-[11px] italic leading-relaxed text-[#8A93A6]"
                    data-testid={`${kind}-rationale-${idx}`}
                >
                    {item.rationale}
                </p>
            )}

            {kind === "caption" && item.hashtags && item.hashtags.length > 0 && (
                <div
                    className="flex flex-wrap gap-1.5 pt-1"
                    data-testid={`${kind}-hashtags-${idx}`}
                >
                    {item.hashtags.map((h, i) => (
                        <span
                            key={i}
                            className="rounded-md border border-[#1F2A40] bg-[#0B0F19] px-2 py-0.5 font-mono text-[10px] text-[#8A93A6]"
                        >
                            {h}
                        </span>
                    ))}
                </div>
            )}
        </motion.div>
    );
}
